/**
 * Error Handler Utility
 * Extracts readable error messages from various error formats
 */

/**
 * Extract a readable error message from any error format
 * Handles: Error objects, API errors, strings, objects, etc.
 * 
 * @param {any} error - The error to extract message from
 * @param {string} fallback - Fallback message if no message found
 * @returns {string} - Readable error message
 */
export const getErrorMessage = (error, fallback = 'An error occurred') => {
  // If error is null or undefined
  if (!error) {
    return fallback;
  }

  // If error is already a string
  if (typeof error === 'string') {
    return error || fallback;
  }

  // If error is a number
  if (typeof error === 'number') {
    return `Error code: ${error}`;
  }

  // If error is an Error object or API error
  if (error instanceof Error) {
    return error.message || fallback;
  }

  // Try common error message properties
  if (error.message) {
    return typeof error.message === 'string' ? error.message : fallback;
  }

  if (error.detail) {
    return typeof error.detail === 'string' ? error.detail : fallback;
  }

  if (error.error) {
    // Recursively extract from nested error
    return getErrorMessage(error.error, fallback);
  }

  if (error.msg) {
    return typeof error.msg === 'string' ? error.msg : fallback;
  }

  // If error has response.data (axios-style)
  if (error.response?.data) {
    return getErrorMessage(error.response.data, fallback);
  }

  // If error has a status code, include it
  if (error.status || error.statusCode) {
    const status = error.status || error.statusCode;
    return `Error ${status}: ${fallback}`;
  }

  // Last resort: try to stringify if it's a simple object
  if (typeof error === 'object') {
    try {
      const keys = Object.keys(error);
      if (keys.length > 0) {
        // Try to find any string value in the object
        for (const key of keys) {
          if (typeof error[key] === 'string' && error[key].length > 0) {
            return error[key];
          }
        }
      }
    } catch (e) {
      // Ignore stringify errors
    }
  }

  // If all else fails, return fallback
  return fallback;
};

/**
 * Format error for display in UI
 * Returns an object with message and optional details
 * 
 * @param {any} error - The error to format
 * @returns {Object} - { message: string, details?: string, status?: number }
 */
export const formatErrorForDisplay = (error) => {
  const message = getErrorMessage(error);
  
  const result = {
    message,
  };

  // Add status code if available
  if (error?.status || error?.statusCode) {
    result.status = error.status || error.statusCode;
  }

  // Add additional details if available
  if (error?.response?.data?.errors) {
    result.details = error.response.data.errors;
  } else if (error?.errors) {
    result.details = error.errors;
  }

  return result;
};

/**
 * Check if error is a network error
 */
export const isNetworkError = (error) => {
  return (
    error?.status === 0 ||
    error?.message?.toLowerCase().includes('network') ||
    error?.message?.toLowerCase().includes('fetch') ||
    error?.name === 'NetworkError'
  );
};

/**
 * Check if error is an authentication error
 */
export const isAuthError = (error) => {
  return error?.status === 401 || error?.statusCode === 401;
};

/**
 * Check if error is an authorization error
 */
export const isForbiddenError = (error) => {
  return error?.status === 403 || error?.statusCode === 403;
};

/**
 * Check if error is a validation error
 */
export const isValidationError = (error) => {
  return error?.status === 400 || error?.statusCode === 400;
};

/**
 * Check if error is a not found error
 */
export const isNotFoundError = (error) => {
  return error?.status === 404 || error?.statusCode === 404;
};

/**
 * Check if error is a rate limit error
 */
export const isRateLimitError = (error) => {
  return error?.status === 429 || error?.statusCode === 429;
};

/**
 * Get user-friendly error title based on error type
 */
export const getErrorTitle = (error) => {
  if (isNetworkError(error)) return 'Connection Error';
  if (isAuthError(error)) return 'Authentication Required';
  if (isForbiddenError(error)) return 'Access Denied';
  if (isValidationError(error)) return 'Validation Error';
  if (isNotFoundError(error)) return 'Not Found';
  if (isRateLimitError(error)) return 'Too Many Requests';
  
  const status = error?.status || error?.statusCode;
  if (status >= 500) return 'Server Error';
  if (status >= 400) return 'Request Error';
  
  return 'Error';
};

/**
 * Safe error logger - prevents [Object Object] in console
 * Only logs in development environment
 */
export const logError = (context, error) => {
  // Only log in development environment
  if (process.env.NEXT_PUBLIC_ENV !== 'development') {
    return;
  }
  
  const message = getErrorMessage(error);
  const errorInfo = formatErrorForDisplay(error);
  
  console.error(`[${context}]`, message);
  
  // Log additional details if available
  if (errorInfo.status) {
    console.error(`Status: ${errorInfo.status}`);
  }
  
  if (errorInfo.details) {
    console.error('Details:', errorInfo.details);
  }
  
  // Log full error object for debugging (but labeled)
  console.error('Full error object:', error);
};

export default {
  getErrorMessage,
  formatErrorForDisplay,
  isNetworkError,
  isAuthError,
  isForbiddenError,
  isValidationError,
  isNotFoundError,
  isRateLimitError,
  getErrorTitle,
  logError,
};
